/**
 * EvidenceCards - Display collected evidence for classification
 * Bioluminescent deep-sea specimen card aesthetic
 */

import Phaser from 'phaser';
import type { EvidenceCard } from '@/types';
import { EvidenceType, Classification } from '@/types';
import { getEvidenceColor, getEvidenceIcon } from '@/data/evidence';
import { COLORS, TIMING, createGlowingPanel, createSectionTitle } from '@/ui/theme/BioluminescentTheme';

export interface EvidenceCardsConfig {
  /** Position X */
  x?: number;
  /** Position Y */
  y?: number;
  /** Width of the panel */
  width?: number;
  /** Maximum cards to display */
  maxCards?: number;
}

interface DisplayedCard {
  container: Phaser.GameObjects.Container;
  evidence: EvidenceCard;
  isNew: boolean;
  glowGraphics?: Phaser.GameObjects.Graphics;
}

/**
 * EvidenceCards displays collected evidence from scanning
 * Enhanced with bioluminescent deep-sea aesthetic
 */
export class EvidenceCards {
  private scene: Phaser.Scene;
  private config: Required<EvidenceCardsConfig>;

  /** Main container */
  private container!: Phaser.GameObjects.Container;

  /** Title container */
  private titleContainer!: Phaser.GameObjects.Container;
  private titleText!: Phaser.GameObjects.Text;
  private countBadge!: Phaser.GameObjects.Container;

  /** Cards container for scrolling */
  private cardsContainer!: Phaser.GameObjects.Container;

  /** Scroll mask for clipping */
  private scrollMask!: Phaser.GameObjects.Graphics;

  /** Scroll state */
  private scrollOffset = 0;
  private maxScrollOffset = 0;
  private scrollIndicator?: Phaser.GameObjects.Container;

  /** Displayed cards */
  private displayedCards: DisplayedCard[] = [];

  /** Card dimensions */
  private static readonly CARD_HEIGHT = 60;
  private static readonly CARD_SPACING = 10;

  /** Classification colors - bioluminescent palette */
  private static readonly CLASSIFICATION_COLORS = {
    living: COLORS.greenGlow,
    nonLiving: COLORS.error,
    unicellular: COLORS.purpleGlow,
    multicellular: COLORS.warmGlow,
  };

  constructor(scene: Phaser.Scene, config: EvidenceCardsConfig = {}) {
    this.scene = scene;

    this.config = {
      x: config.x ?? 20,
      y: config.y ?? 20,
      width: config.width ?? 300,
      maxCards: config.maxCards ?? 5,
    };

    this.create();
    this.setupEventListeners();
  }

  /**
   * Create the evidence cards panel
   */
  private create(): void {
    this.container = this.scene.add.container(this.config.x, this.config.y);
    this.container.setDepth(1000);

    this.createPanel();
    this.createTitle();
    this.createCardsContainer();
  }

  /**
   * Create panel background using theme
   */
  private createPanel(): void {
    const { width } = this.config;
    const height = this.getPanelHeight();

    const panel = createGlowingPanel(this.scene, width, height, {
      glowColor: COLORS.tealGlow,
      glowIntensity: 0.2,
      cornerRadius: 14,
      borderWidth: 2,
      hasInnerGlow: true,
      hasCornerAccents: true,
      hasScanlines: false,
    });

    this.container.add(panel);
  }

  /**
   * Calculate panel height based on max cards
   */
  private getPanelHeight(): number {
    return 50 + (EvidenceCards.CARD_HEIGHT + EvidenceCards.CARD_SPACING) * this.config.maxCards + 15;
  }

  /**
   * Create panel title with count badge
   */
  private createTitle(): void {
    // Title section with decorative elements
    this.titleContainer = createSectionTitle(
      this.scene,
      'COLLECTED EVIDENCE',
      this.config.width - 70,
      {
        icon: '',
        color: '#00ffc8',
        accentColor: COLORS.tealGlow,
        fontSize: 11,
      }
    );
    this.titleContainer.setPosition(14, 12);
    this.container.add(this.titleContainer);

    // Count badge (glowing pill)
    this.countBadge = this.scene.add.container(this.config.width - 45, 16);

    const badgeBg = this.scene.add.graphics();
    badgeBg.fillStyle(COLORS.tealGlow, 0.2);
    badgeBg.fillRoundedRect(0, 0, 32, 18, 9);
    badgeBg.lineStyle(1, COLORS.tealGlow, 0.5);
    badgeBg.strokeRoundedRect(0, 0, 32, 18, 9);
    this.countBadge.add(badgeBg);

    this.titleText = this.scene.add.text(16, 9, '0', {
      fontFamily: '"JetBrains Mono", "Fira Code", monospace',
      fontSize: '11px',
      color: '#00ffc8',
      fontStyle: 'bold',
    });
    this.titleText.setOrigin(0.5, 0.5);
    this.countBadge.add(this.titleText);

    this.container.add(this.countBadge);

    // Decorative line under title
    const divider = this.scene.add.graphics();
    divider.lineStyle(1, COLORS.tealGlow, 0.3);
    divider.lineBetween(14, 38, this.config.width - 14, 38);
    this.container.add(divider);
  }

  /**
   * Create container for cards with scroll masking
   */
  private createCardsContainer(): void {
    this.cardsContainer = this.scene.add.container(12, 48);
    this.container.add(this.cardsContainer);

    // Create mask for clipping cards outside visible area
    const maskX = this.config.x + 12;
    const maskY = this.config.y + 48;
    const maskWidth = this.config.width - 24;
    const maskHeight = (EvidenceCards.CARD_HEIGHT + EvidenceCards.CARD_SPACING) * this.config.maxCards - EvidenceCards.CARD_SPACING + 10;

    this.scrollMask = this.scene.add.graphics();
    this.scrollMask.fillStyle(0xffffff);
    this.scrollMask.fillRect(maskX, maskY, maskWidth, maskHeight);
    this.scrollMask.setVisible(false);

    const mask = this.scrollMask.createGeometryMask();
    this.cardsContainer.setMask(mask);

    // Create scroll indicator
    this.createScrollIndicator();

    // Add mouse wheel scrolling
    this.setupScrollInput();
  }

  /**
   * Create scroll indicator (draggable scrollbar)
   */
  private createScrollIndicator(): void {
    const { width } = this.config;
    const indicatorX = width - 16;
    // Position scrollbar to span the full cards area height
    const cardsAreaHeight = (EvidenceCards.CARD_HEIGHT + EvidenceCards.CARD_SPACING) * this.config.maxCards;
    const indicatorY = 48 + cardsAreaHeight / 2;
    const trackHeight = cardsAreaHeight - 20;

    this.scrollIndicator = this.scene.add.container(indicatorX, indicatorY);

    // Scroll track (full height, clickable for quick scroll)
    const track = this.scene.add.graphics();
    track.fillStyle(COLORS.dataHidden, 0.2);
    track.fillRoundedRect(-4, -trackHeight / 2, 8, trackHeight, 4);
    track.setName('track');
    this.scrollIndicator.add(track);

    // Make track interactive for click-to-scroll
    const trackHitArea = this.scene.add.rectangle(0, 0, 16, trackHeight, 0x000000, 0);
    trackHitArea.setInteractive({ useHandCursor: true });
    trackHitArea.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (this.maxScrollOffset <= 0) return;
      const localY = pointer.y - this.container.y - indicatorY;
      const scrollRatio = (localY + trackHeight / 2) / trackHeight;
      this.scrollTo(scrollRatio * this.maxScrollOffset);
    });
    this.scrollIndicator.add(trackHitArea);

    // Scroll thumb (draggable)
    const thumbHeight = trackHeight * 0.3; // Will be updated dynamically
    const thumb = this.scene.add.graphics();
    thumb.fillStyle(COLORS.tealGlow, 0.6);
    thumb.fillRoundedRect(-3, -thumbHeight / 2, 6, thumbHeight, 3);
    thumb.setName('thumb');
    thumb.setData('trackHeight', trackHeight);
    this.scrollIndicator.add(thumb);

    // Make thumb draggable
    const thumbHitArea = this.scene.add.rectangle(0, 0, 14, thumbHeight, 0x000000, 0);
    thumbHitArea.setInteractive({ useHandCursor: true, draggable: true });
    thumbHitArea.setName('thumbHitArea');
    thumbHitArea.setData('trackHeight', trackHeight);
    this.scrollIndicator.add(thumbHitArea);

    // Drag handling
    this.scene.input.setDraggable(thumbHitArea);

    thumbHitArea.on('drag', (_pointer: Phaser.Input.Pointer, _dragX: number, dragY: number) => {
      if (this.maxScrollOffset <= 0) return;

      const thumbData = thumbHitArea.getData('thumbHeight') as number || thumbHeight;
      const minY = -trackHeight / 2 + thumbData / 2;
      const maxY = trackHeight / 2 - thumbData / 2;
      const clampedY = Phaser.Math.Clamp(dragY, minY, maxY);

      const scrollRatio = (clampedY - minY) / (maxY - minY);
      this.scrollTo(scrollRatio * this.maxScrollOffset);
    });

    // Hover effect on thumb
    thumbHitArea.on('pointerover', () => {
      thumb.clear();
      const currentThumbHeight = thumbHitArea.getData('thumbHeight') as number || thumbHeight;
      const thumbY = thumbHitArea.y;
      thumb.fillStyle(COLORS.tealGlow, 0.9);
      thumb.fillRoundedRect(-3, thumbY - currentThumbHeight / 2, 6, currentThumbHeight, 3);
    });

    thumbHitArea.on('pointerout', () => {
      this.updateScrollIndicator();
    });

    this.scrollIndicator.setAlpha(0);
    this.container.add(this.scrollIndicator);
  }

  /**
   * Setup scroll input handling
   */
  private setupScrollInput(): void {
    // Mouse wheel scrolling within panel bounds
    this.scene.input.on('wheel', (_pointer: Phaser.Input.Pointer, _gameObjects: Phaser.GameObjects.GameObject[], _deltaX: number, deltaY: number) => {
      if (this.maxScrollOffset <= 0) return;

      // Check if mouse is over the panel
      const pointer = this.scene.input.activePointer;
      const panelBounds = new Phaser.Geom.Rectangle(
        this.config.x,
        this.config.y,
        this.config.width,
        this.getPanelHeight()
      );

      if (panelBounds.contains(pointer.x, pointer.y)) {
        const scrollAmount = deltaY > 0 ? 30 : -30;
        this.scrollTo(this.scrollOffset + scrollAmount);
      }
    });
  }

  /**
   * Scroll to a specific offset
   */
  private scrollTo(offset: number): void {
    this.scrollOffset = Phaser.Math.Clamp(offset, 0, this.maxScrollOffset);

    this.scene.tweens.add({
      targets: this.cardsContainer,
      y: 48 - this.scrollOffset,
      duration: 100,
      ease: 'Sine.easeOut',
    });

    this.updateScrollIndicator();
  }

  /**
   * Update scroll indicator position and visibility
   */
  private updateScrollIndicator(): void {
    if (!this.scrollIndicator) return;

    if (this.maxScrollOffset <= 0) {
      // No scrolling needed
      this.scene.tweens.add({
        targets: this.scrollIndicator,
        alpha: 0,
        duration: TIMING.fast,
      });
      return;
    }

    // Show indicator
    this.scene.tweens.add({
      targets: this.scrollIndicator,
      alpha: 1,
      duration: TIMING.fast,
    });

    // Get thumb elements
    const thumb = this.scrollIndicator.list.find(child => child.name === 'thumb') as Phaser.GameObjects.Graphics;
    const thumbHitArea = this.scrollIndicator.list.find(child => child.name === 'thumbHitArea') as Phaser.GameObjects.Rectangle;

    if (thumb && thumbHitArea) {
      const trackHeight = thumb.getData('trackHeight') as number || 200;
      const visibleRatio = Math.min(1, this.config.maxCards / this.displayedCards.length);
      const thumbHeight = Math.max(30, trackHeight * visibleRatio);
      const scrollRatio = this.maxScrollOffset > 0 ? this.scrollOffset / this.maxScrollOffset : 0;

      const minY = -trackHeight / 2 + thumbHeight / 2;
      const maxY = trackHeight / 2 - thumbHeight / 2;
      const thumbY = minY + scrollRatio * (maxY - minY);

      // Update thumb graphics
      thumb.clear();
      thumb.fillStyle(COLORS.tealGlow, 0.6);
      thumb.fillRoundedRect(-3, thumbY - thumbHeight / 2, 6, thumbHeight, 3);

      // Update thumb hit area
      thumbHitArea.setY(thumbY);
      thumbHitArea.setSize(14, thumbHeight);
      thumbHitArea.setData('thumbHeight', thumbHeight);
    }
  }

  /**
   * Set up event listeners
   */
  private setupEventListeners(): void {
    this.scene.events.on('classification:evidence:added', this.handleEvidenceAdded, this);
    this.scene.events.on('classification:evidence:cleared', this.handleEvidenceCleared, this);
  }

  /**
   * Handle evidence added
   */
  private handleEvidenceAdded(evidence: EvidenceCard): void {
    this.addCard(evidence, true);
  }

  /**
   * Handle evidence cleared
   */
  private handleEvidenceCleared(): void {
    this.clearCards();
  }

  /**
   * Add an evidence card
   */
  public addCard(evidence: EvidenceCard, isNew: boolean = false): void {
    // Check if already displayed
    if (this.displayedCards.some(dc => dc.evidence.id === evidence.id)) {
      return;
    }

    // Create card
    const card = this.createCard(evidence, isNew);
    const yPos = this.displayedCards.length * (EvidenceCards.CARD_HEIGHT + EvidenceCards.CARD_SPACING);
    card.container.setY(yPos);

    this.cardsContainer.add(card.container);
    this.displayedCards.push(card);

    // Update title
    this.updateTitle();

    // Animate if new
    if (isNew) {
      this.animateNewCard(card);
    }

    // Scroll if needed
    if (this.displayedCards.length > this.config.maxCards) {
      this.scrollCards();
    }
  }

  /**
   * Create a single evidence card with bioluminescent styling
   */
  private createCard(evidence: EvidenceCard, isNew: boolean): DisplayedCard {
    const { width } = this.config;
    const cardWidth = width - 24;
    const cardHeight = EvidenceCards.CARD_HEIGHT;

    const container = this.scene.add.container(0, 0);
    const typeColor = getEvidenceColor(evidence.type);

    // Card glow effect (for new cards)
    const glowGraphics = this.scene.add.graphics();
    if (isNew) {
      glowGraphics.fillStyle(typeColor, 0.15);
      glowGraphics.fillRoundedRect(-3, -3, cardWidth + 6, cardHeight + 6, 10);
    }
    container.add(glowGraphics);

    // Card background with gradient effect
    const bg = this.scene.add.graphics();

    // Shadow
    bg.fillStyle(0x000000, 0.4);
    bg.fillRoundedRect(2, 2, cardWidth, cardHeight, 8);

    // Main background
    bg.fillStyle(COLORS.panelBg, 0.95);
    bg.fillRoundedRect(0, 0, cardWidth, cardHeight, 8);

    // Top gradient highlight
    bg.fillStyle(COLORS.twilight, 0.3);
    bg.fillRoundedRect(0, 0, cardWidth, cardHeight * 0.4, { tl: 8, tr: 8, bl: 0, br: 0 });

    // Border
    bg.lineStyle(1, COLORS.panelBorder, 0.6);
    bg.strokeRoundedRect(0, 0, cardWidth, cardHeight, 8);
    container.add(bg);

    // Type indicator bar (left side) with glow
    const typeBar = this.scene.add.graphics();
    // Glow effect
    typeBar.fillStyle(typeColor, 0.3);
    typeBar.fillRoundedRect(-2, 4, 6, cardHeight - 8, 3);
    // Main bar
    typeBar.fillStyle(typeColor, 1);
    typeBar.fillRoundedRect(0, 6, 3, cardHeight - 12, 2);
    container.add(typeBar);

    // Type icon with glow
    const typeIcon = this.scene.add.text(14, 8, getEvidenceIcon(evidence.type), {
      fontFamily: '"Segoe UI Emoji", "Apple Color Emoji", sans-serif',
      fontSize: '13px',
    });
    container.add(typeIcon);

    // Type label
    const typeLabel = this.scene.add.text(32, 8, this.formatEvidenceType(evidence.type), {
      fontFamily: '"JetBrains Mono", "Fira Code", monospace',
      fontSize: '9px',
      color: `#${typeColor.toString(16).padStart(6, '0')}`,
      letterSpacing: 1,
    });
    container.add(typeLabel);

    // Description text
    const description = this.scene.add.text(14, 26, evidence.description, {
      fontFamily: '"JetBrains Mono", "Fira Code", monospace',
      fontSize: '10px',
      color: '#f0f9ff',
      wordWrap: { width: cardWidth - 28 },
      lineSpacing: 2,
    });
    container.add(description);

    // Classification support indicators
    const supportContainer = this.createSupportIndicators(evidence, cardWidth - 10, cardHeight - 14);
    container.add(supportContainer);

    return { container, evidence, isNew, glowGraphics };
  }

  /**
   * Create classification support indicators with glowing badges
   */
  private createSupportIndicators(
    evidence: EvidenceCard,
    x: number,
    y: number
  ): Phaser.GameObjects.Container {
    const container = this.scene.add.container(x, y);
    const badges: Phaser.GameObjects.Container[] = [];

    // Add support indicators as glowing badges
    for (const classification of evidence.supportsClassification) {
      const badge = this.createSupportBadge(classification);
      badges.push(badge);
    }

    // Position from right to left
    let offsetX = 0;
    for (let i = badges.length - 1; i >= 0; i--) {
      badges[i].setX(-offsetX);
      const badgeWidth = 36; // Approximate badge width
      offsetX += badgeWidth + 4;
      container.add(badges[i]);
    }

    return container;
  }

  /**
   * Create a single support badge
   */
  private createSupportBadge(classification: Classification): Phaser.GameObjects.Container {
    const badge = this.scene.add.container(0, 0);
    const color = this.getClassificationColor(classification);
    const label = this.getClassificationShort(classification, true);

    // Badge background with glow
    const bg = this.scene.add.graphics();
    bg.fillStyle(color, 0.15);
    bg.fillRoundedRect(-34, -8, 34, 16, 8);
    bg.lineStyle(1, color, 0.5);
    bg.strokeRoundedRect(-34, -8, 34, 16, 8);
    badge.add(bg);

    // Badge text
    const text = this.scene.add.text(-17, 0, label, {
      fontFamily: '"JetBrains Mono", "Fira Code", monospace',
      fontSize: '8px',
      color: `#${color.toString(16).padStart(6, '0')}`,
      fontStyle: 'bold',
    });
    text.setOrigin(0.5, 0.5);
    badge.add(text);

    return badge;
  }

  /**
   * Get short classification label
   */
  private getClassificationShort(classification: Classification, support: boolean): string {
    const prefix = support ? '+' : '-';
    switch (classification) {
      case Classification.LIVING:
        return `${prefix}LIV`;
      case Classification.NON_LIVING:
        return `${prefix}NON`;
      case Classification.UNICELLULAR:
        return `${prefix}UNI`;
      case Classification.MULTICELLULAR:
        return `${prefix}MUL`;
      default:
        return '';
    }
  }

  /**
   * Get classification color from theme
   */
  private getClassificationColor(classification: Classification): number {
    switch (classification) {
      case Classification.LIVING:
        return EvidenceCards.CLASSIFICATION_COLORS.living;
      case Classification.NON_LIVING:
        return EvidenceCards.CLASSIFICATION_COLORS.nonLiving;
      case Classification.UNICELLULAR:
        return EvidenceCards.CLASSIFICATION_COLORS.unicellular;
      case Classification.MULTICELLULAR:
        return EvidenceCards.CLASSIFICATION_COLORS.multicellular;
      default:
        return COLORS.textMuted;
    }
  }

  /**
   * Format evidence type for display
   */
  private formatEvidenceType(type: EvidenceType): string {
    switch (type) {
      case EvidenceType.STRUCTURAL:
        return 'STRUCTURAL';
      case EvidenceType.FUNCTIONAL:
        return 'FUNCTIONAL';
      case EvidenceType.COMPOSITIONAL:
        return 'COMPOSITIONAL';
      case EvidenceType.BEHAVIORAL:
        return 'BEHAVIORAL';
      case EvidenceType.CONTEXTUAL:
        return 'CONTEXTUAL';
      default:
        return 'EVIDENCE';
    }
  }

  /**
   * Animate new card appearance with bioluminescent effects
   */
  private animateNewCard(card: DisplayedCard): void {
    card.container.setAlpha(0);
    card.container.setScale(0.9);
    card.container.x = -15;

    // Slide in and scale up
    this.scene.tweens.add({
      targets: card.container,
      alpha: 1,
      scaleX: 1,
      scaleY: 1,
      x: 0,
      duration: TIMING.normal,
      ease: 'Back.easeOut',
      onComplete: () => {
        // Fade out the glow effect
        if (card.glowGraphics) {
          this.scene.tweens.add({
            targets: card.glowGraphics,
            alpha: 0,
            duration: TIMING.gentle,
            onComplete: () => {
              card.isNew = false;
            },
          });
        } else {
          card.isNew = false;
        }
      },
    });

    // Pulse the count badge
    this.scene.tweens.add({
      targets: this.countBadge,
      scaleX: 1.2,
      scaleY: 1.2,
      duration: TIMING.fast,
      yoyo: true,
      ease: 'Sine.easeOut',
    });
  }

  /**
   * Update scroll bounds and auto-scroll to show new cards
   */
  private scrollCards(): void {
    const totalCardsHeight = this.displayedCards.length * (EvidenceCards.CARD_HEIGHT + EvidenceCards.CARD_SPACING);
    const visibleHeight = this.config.maxCards * (EvidenceCards.CARD_HEIGHT + EvidenceCards.CARD_SPACING);

    // Calculate max scroll offset
    this.maxScrollOffset = Math.max(0, totalCardsHeight - visibleHeight);

    // Auto-scroll to show the newest card
    if (this.maxScrollOffset > 0) {
      this.scrollTo(this.maxScrollOffset);
    }

    this.updateScrollIndicator();
  }

  /**
   * Update title with card count (animates badge)
   */
  private updateTitle(): void {
    const newCount = this.displayedCards.length.toString();
    const currentCount = this.titleText.text;

    if (newCount !== currentCount) {
      // Animate the count change
      this.scene.tweens.add({
        targets: this.titleText,
        scaleX: 0,
        scaleY: 0,
        duration: TIMING.fast / 2,
        ease: 'Power2',
        onComplete: () => {
          this.titleText.setText(newCount);
          this.scene.tweens.add({
            targets: this.titleText,
            scaleX: 1,
            scaleY: 1,
            duration: TIMING.fast / 2,
            ease: 'Back.easeOut',
          });
        },
      });
    }
  }

  /**
   * Clear all cards with animation
   */
  public clearCards(): void {
    // Animate cards out
    this.displayedCards.forEach((card, index) => {
      this.scene.tweens.add({
        targets: card.container,
        alpha: 0,
        x: -20,
        scaleX: 0.8,
        scaleY: 0.8,
        duration: TIMING.fast,
        delay: index * 50,
        ease: 'Power2',
        onComplete: () => card.container.destroy(),
      });
    });

    this.displayedCards = [];
    this.cardsContainer.setY(48);
    this.scrollOffset = 0;
    this.maxScrollOffset = 0;
    this.updateTitle();
    this.updateScrollIndicator();
  }

  /**
   * Get all displayed evidence
   */
  public getEvidence(): EvidenceCard[] {
    return this.displayedCards.map(dc => dc.evidence);
  }

  /**
   * Set visibility
   */
  public setVisible(visible: boolean): void {
    this.container.setVisible(visible);
  }

  /**
   * Set position
   */
  public setPosition(x: number, y: number): void {
    this.container.setPosition(x, y);
  }

  /**
   * Get the container
   */
  public getContainer(): Phaser.GameObjects.Container {
    return this.container;
  }

  /**
   * Clean up
   */
  public destroy(): void {
    this.scene.events.off('classification:evidence:added', this.handleEvidenceAdded, this);
    this.scene.events.off('classification:evidence:cleared', this.handleEvidenceCleared, this);

    // Clean up mask
    if (this.scrollMask) {
      this.scrollMask.destroy();
    }

    this.clearCards();
    this.container.destroy();
  }
}
